import React, { useContext, useState } from 'react';
import { FormControlLabel, Chip, RadioGroup, Radio, Box, TextField } from '@mui/material';
import AtomicButton from '../../../../atomic/button/Button';
import SelectBox from '../../../../atomic/selectbox/SelectBox';
import { statusTypeLabel, statusTypeColor } from '../constants';
import { SectionCard } from './SectionCard';
import styles from './styles.module.css';
import { ProductDataContext } from '../../../../context-api/data-provider/ProductDataProvider';
import variables from '../../../../utils/assets/_variables.scss';

const statusSectionCardStyle = {
  border: `2px solid ${variables.backgroundDarkBlue}`,
};

const merchantRadioStyle = {
  color: 'rgb(24, 20, 54)',
  '&.Mui-checked': {
    color: 'rgb(24, 20, 54)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
};

const chipSx = (typeColor) => ({
  border: `1px solid ${typeColor}`,
  background: typeColor,
  color: 'white',
  height: '26px',
  fontSize: '12px',
  fontFamily: 'Inter Medium',
  borderRadius: '6px',
  fontWeight: 600,
  '& .MuiChip-label': {
    px: 1.25,
  },
});

/** Rejection reason SelectBox — background only via FormControl (SelectBox supports `formControlSx`). */
const rejectionReasonSelectFormControlSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#ffffff',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
};

/** Normalize API `status_type` for comparisons. */
const normalizeStatusType = (value) => String(value ?? '').trim().toLowerCase();

/** When `status_type` is one of these, this component renders nothing at all (`return null`). */
const HIDE_STATUS_SECTION = new Set(['approved', 'admin_review_pending']);

const NOTES_ONLY = new Set([
  'admin_rejected',
  'merchant_approved',
  'merchant_rejected',
]);

const MERCHANT_DECISION_INTERACTIVE = new Set([
  'merchant_review_pending',
  'ai_generated_merchant_review_pending',
]);

/** Shown when merchant selects "Merchant rejected". Last option opens a custom note field. */
const MERCHANT_REJECTION_REASONS = [
  { value: 'image_quality', label: 'Image quality or clarity issue' },
  { value: 'incorrect_info', label: 'Product information is incorrect' },
  { value: 'policy', label: 'Does not meet listing policy' },
  { value: '__custom__', label: 'Add custom note' },
];

const CUSTOM_REJECTION_VALUE = '__custom__';

/** Placeholder value for SelectBox (same pattern as Signup `SelectBox`). */
const REJECTION_UNSELECTED = '__unselected__';

const REJECTION_SELECT_OPTIONS = [
  REJECTION_UNSELECTED,
  ...MERCHANT_REJECTION_REASONS.map((r) => r.value),
];

const formatRejectionOptionLabel = (opt) => {
  if (opt === REJECTION_UNSELECTED) return 'Select a reason';
  return MERCHANT_REJECTION_REASONS.find((r) => r.value === opt)?.label ?? opt;
};

function getStatusSectionMode(normalized) {
  if (!normalized) return 'hidden';
  if (HIDE_STATUS_SECTION.has(normalized)) return 'hidden';
  if (MERCHANT_DECISION_INTERACTIVE.has(normalized)) return 'interactive';
  if (NOTES_ONLY.has(normalized)) return 'notes_only';
  return 'notes_only';
}

export const StatusUpdateSection = ({ onRefresh, onClose, product }) => {
  const { onUpdateProductStatus, updateProductStatusData } = useContext(ProductDataContext);
  const [merchantStatusSelection, setMerchantStatusSelection] = useState('');
  const [rejectionReasonKey, setRejectionReasonKey] = useState('');
  const [customStatusNote, setCustomStatusNote] = useState('');
  const normalizedType = normalizeStatusType(product?.status_type);
  const mode = getStatusSectionMode(normalizedType);

  const typeLabel = statusTypeLabel[normalizedType] || 'Unknown status';
  const typeColor = statusTypeColor[normalizedType] || '#999';

  // 'approved', // will not show status component
  // 'admin_review_pending', // will not show status component
  // 'admin_rejected  // we will not show radio options and update button,  only the status_notes in product object
  // 'merchant_approved' // we will not show radio options and update button ,   only the status_notes in product object text
  // 'merchant_rejected' // we will not show radio options and update button ,   only the status_notes in product object text
  // 'merchant_review_pending    //  radio option to approve or rject with button
  // 'ai_generated_merchant_review_pending',    //  radio option to approve or rject with button

  const statusUpdateLoading = !!updateProductStatusData?.loading;

  const resolveStatusNotesForPayload = () => {
    if (merchantStatusSelection !== 'merchant_rejected') {
      return '';
    }
    if (rejectionReasonKey === CUSTOM_REJECTION_VALUE) {
      return customStatusNote.trim();
    }
    const found = MERCHANT_REJECTION_REASONS.find((r) => r.value === rejectionReasonKey);
    return found ? found.label : '';
  };

  const rejectionNotesComplete =
    merchantStatusSelection !== 'merchant_rejected' ||
    (rejectionReasonKey &&
      (rejectionReasonKey !== CUSTOM_REJECTION_VALUE || customStatusNote.trim() !== ''));

  const handleMerchantStatusSubmit = async () => {
    if (!merchantStatusSelection || !rejectionNotesComplete) return;
    const result = await onUpdateProductStatus?.({
      kitchen_name: product?.kitchen_name,
      product_sku: product?.product_sku,
      status_type: merchantStatusSelection,
      status_notes: resolveStatusNotesForPayload(),
      user_acount: {
        name: product?.kitchen_name,
      },
    });
    if (result?.data?.success) {
      setMerchantStatusSelection('');
      setRejectionReasonKey('');
      setCustomStatusNote('');
      onRefresh?.();
      onClose?.();
    }
  };

  const handleMerchantDecisionChange = (e) => {
    const value = e.target.value;
    setMerchantStatusSelection(value);
    setRejectionReasonKey('');
    setCustomStatusNote('');
  };

  const handleRejectionReasonChange = (val) => {
    const next = val === REJECTION_UNSELECTED ? '' : val;
    setRejectionReasonKey(next);
    if (next !== CUSTOM_REJECTION_VALUE) {
      setCustomStatusNote('');
    }
  };

  if (mode === 'hidden') {
    return null;
  }

  const statusNotesText = (product?.status_notes ?? '').trim();

  const statusChip = (
    <Chip label={typeLabel} size='small' sx={chipSx(typeColor)} />
  );

  if (mode === 'notes_only') {
    return (
      <SectionCard title='Product status' style={statusSectionCardStyle}>
        <div className={styles.statusCardBody}>
          <div className={styles.statusBadgeRow}>
            <span className={styles.fieldLabel}>Workflow status</span>
            {statusChip}
          </div>
          <div className={styles.statusSection}>
            <span className={styles.fieldLabel}>Status notes</span>
            {statusNotesText ? (
              <div className={styles.statusNote}>{statusNotesText}</div>
            ) : (
              <div className={styles.statusNote} style={{ color: '#aaa', fontStyle: 'italic' }}>
                No status notes.
              </div>
            )}
          </div>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title='Product status' style={statusSectionCardStyle}>
      <div className={styles.statusCardBody}>
        <div className={styles.statusBadgeRow}>
          <span className={styles.fieldLabel}>Workflow status</span>
          {statusChip}
        </div>

        <div className={styles.statusDecisionPanel}>
          <span className={styles.fieldLabel}>Merchant decision</span>
          <p className={styles.statusDecisionHint}>
            Choose approve or reject. If you reject, pick a reason or add a custom note.
          </p>

          <RadioGroup
            name='merchant_status_decision'
            value={merchantStatusSelection}
            onChange={handleMerchantDecisionChange}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <FormControlLabel
                value='merchant_approved'
                control={<Radio sx={merchantRadioStyle} />}
                label='Merchant approved'
                sx={{
                  margin: 0,
                  alignItems: 'center',
                  py: 0.25,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Inter Medium',
                    color: 'rgb(24, 20, 54)',
                  },
                }}
              />
              <FormControlLabel
                value='merchant_rejected'
                control={<Radio sx={merchantRadioStyle} />}
                label='Merchant rejected'
                sx={{
                  margin: 0,
                  alignItems: 'center',
                  py: 0.25,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Inter Medium',
                    color: 'rgb(24, 20, 54)',
                  },
                }}
              />
            </Box>
          </RadioGroup>

          {merchantStatusSelection === 'merchant_rejected' && (
            <Box sx={{ width: '100%', pt: 0.5 }}>
              <Box sx={{ mb: rejectionReasonKey === CUSTOM_REJECTION_VALUE ? 1.5 : 0 }}>
                <SelectBox
                  sx={{ width: '100%', height: 'auto', margin: 0 }}
                  formControlSx={rejectionReasonSelectFormControlSx}
                  label='Rejection reason'
                  value={rejectionReasonKey || REJECTION_UNSELECTED}
                  onChange={handleRejectionReasonChange}
                  options={REJECTION_SELECT_OPTIONS}
                  formatOptionLabel={formatRejectionOptionLabel}
                />
              </Box>

              {rejectionReasonKey === CUSTOM_REJECTION_VALUE && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder='Enter your note…'
                  value={customStatusNote}
                  onChange={(e) => setCustomStatusNote(e.target.value)}
                  variant='outlined'
                  size='small'
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      fontFamily: 'Inter Regular',
                      backgroundColor: '#fff',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e0e0e8',
                    },
                  }}
                />
              )}
            </Box>
          )}
        </div>

        <div className={styles.statusButtonRow}>
          <AtomicButton
            label='Update product status'
            handleSubmit={handleMerchantStatusSubmit}
            style={{ width: '100%', height: '48px', marginTop: 0, marginBottom: 0 }}
            loadingButtonStyle={{
              backgroundColor: '#e8e8e8',
              color: '#707070',
              border: '1px solid #d0d0d0',
              cursor: 'not-allowed',
            }}
            loading={statusUpdateLoading}
            disabled={
              statusUpdateLoading || !merchantStatusSelection || !rejectionNotesComplete
            }
          />
        </div>
      </div>
    </SectionCard>
  );
};
